import { z } from "zod";
import { cleanParamValue, parsePhoneBR, readAdIdentitySnapshot, readTrackingSnapshot } from "./tracking";
import { resolveOrigin } from "./origin";


export const leadSchema = z.object({
  name: z.string().trim().min(2, "Informe seu nome").max(120),
  email: z.string().trim().email("E-mail inválido").max(200),
  phone: z
    .string()
    .trim()
    .min(8, "Telefone inválido")
    .max(25)
    .regex(/^[\d\s()+\-]+$/, "Use apenas números e símbolos de telefone"),
  company: z.string().trim().max(200).optional().or(z.literal("")),
  message: z.string().trim().max(2000).optional().or(z.literal("")),
});

export type LeadInput = z.infer<typeof leadSchema>;

export type LeadSource =
  | "hero"
  | "contact"
  | "whatsapp"
  | "ia"
  | "cases"
  | "sobre"
  | "parceria"
  | "magistral"
  | "forms1"
  | "forms-full"
  | "chatgpt";

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_id",
  "utm_term",
  "utm_content",
] as const;

/**
 * Schema do payload final enviado ao webhook.
 * - Campos de usuário (name/email/phone) são obrigatórios e não vazios.
 * - company/message podem ser vazios, mas devem existir como string.
 * - Todos os campos automáticos (utm_* e capture_*) são strings — vazias quando ausentes.
 * Qualquer chave extra é removida via `.strict()` para garantir contrato estável.
 */
export const webhookPayloadSchema = z
  .object({
    // Lead + UTMs aninhados sob `fields` ({{ fields.xxx }}).
    fields: z
      .object({
        name: z.string().trim().min(1).max(120),
        company: z.string().max(200),
        phone: z.string().trim().min(1).max(25),
        phone_e164: z.string(),
        phone_ddd: z.string(),
        email: z.string().trim().email().max(200),
        message: z.string().max(2000),
        forms: z.string().max(6000),
        source: z.string().min(1),
        form_source: z.string().min(1),
        attribution_method: z.string(),
        is_campaign: z.string(),
        origin_page: z.string(),
        previous_page: z.string(),
        landing_path: z.string(),
        locale: z.string(),

        // UTMs
        utm_source: z.string(),
        utm_medium: z.string(),
        utm_campaign: z.string(),
        utm_id: z.string(),
        utm_term: z.string(),
        utm_content: z.string(),
        utm_url: z.string(),
        // Atribuição extra
        referrer: z.string(),
        referrer_domain: z.string(),
        landing_page: z.string(),
        landing_timestamp: z.string(),
        landing_host: z.string(),
        page_host: z.string(),
        gclid: z.string(),
        fbclid: z.string(),
        msclkid: z.string(),
        ttclid: z.string(),
        wbraid: z.string(),
        gbraid: z.string(),
        oppref: z.string(),
        ad_account_id: z.string(),
        // Identidade publicitária / analítica
        fbp: z.string(),
        fbc: z.string(),
        ga_client_id: z.string(),
        ga_session_id: z.string(),
        page_title: z.string(),
        consent_analytics: z.string(),
        consent_marketing: z.string(),
        // Identidade
        visitor_id: z.string(),
        session_id: z.string(),
        is_returning_visitor: z.string(),
        visit_count: z.string(),
        // Dispositivo
        device_type: z.string(),
        os: z.string(),
        browser: z.string(),
        viewport_size: z.string(),
        screen_resolution: z.string(),
        // Localização
        timezone: z.string(),
        // Comportamento
        time_on_site_seconds: z.string(),
        scroll_depth_percent: z.string(),
        form_fill_time_seconds: z.string(),
      })
      .strict(),
    // Metadados da captura no nível raiz ({{ page_url }}, etc.)
    page_url: z.string(),
    pathname: z.string(),
    language: z.string(),
    timestamp: z.string().min(1),
    form_id: z.string().min(1),
    user_agent: z.string(),
  })
  .strict();

export type WebhookPayload = z.infer<typeof webhookPayloadSchema>;

const UTM_STORAGE_KEY = "kentro_utm_v1";

/**
 * Lê UTMs do `window.location.search` e persiste em sessionStorage no
 * primeiro hit com parâmetros. Em navegações subsequentes (sem UTM na URL),
 * recupera os valores salvos — garantindo que o lead carregue o rastreio
 * de origem mesmo após trocar de página dentro do site.
 */
export function readUtms(): Record<string, string> {
  const out: Record<string, string> = {};
  for (const k of UTM_KEYS) out[k] = "";
  out.utm_url = "";
  if (typeof window === "undefined") return out;

  // 1) Recupera UTMs persistidos da primeira visita.
  try {
    const stored = window.sessionStorage?.getItem(UTM_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Record<string, string>;
      for (const k of UTM_KEYS) if (parsed[k]) out[k] = parsed[k];
      if (parsed.utm_url) out.utm_url = parsed.utm_url;
    }
  } catch {
    /* sessionStorage indisponível — ignora */
  }

  // 2) Se a URL atual trouxer UTMs, sobrescreve e persiste.
  const params = new URLSearchParams(window.location.search);
  let hasNew = false;
  for (const k of UTM_KEYS) {
    const v = cleanParamValue(params.get(k));
    if (v) {
      out[k] = v;
      hasNew = true;
    }
  }
  if (hasNew) {
    out.utm_url = window.location.href;
    try {
      window.sessionStorage?.setItem(UTM_STORAGE_KEY, JSON.stringify(out));
    } catch {
      /* ignora */
    }
  } else if (!out.utm_url) {
    out.utm_url = window.location.href;
  }
  return out;
}

function readCaptureMeta(source: LeadSource): Record<string, string> {
  if (typeof window === "undefined") {
    return {
      page_url: "",
      pathname: "",
      language: "",
      timestamp: new Date().toISOString(),
      form_id: `kentro-${source}`,
      user_agent: "",
    };
  }
  return {
    page_url: window.location.href || "",
    pathname: window.location.pathname || "",
    language: navigator.language || "",
    timestamp: new Date().toISOString(),
    form_id: `kentro-${source}`,
    user_agent: navigator.userAgent || "",
  };
}

/**
 * Conjunto padrão de variáveis de rastreio (origem resolvida + UTMs + click IDs
 * + identidade publicitária + dispositivo/comportamento) para reutilizar em
 * formulários que possuem webhook próprio (parceria, calculadora, etc.).
 */
export function buildTrackingFields(formId: string, formSource: string): Record<string, string> {
  const origin = resolveOrigin();
  const out: Record<string, string> = {
    source: origin.source,
    form_source: formSource,
    attribution_method: origin.attribution_method,
    is_campaign: String(origin.isCampaign),
    origin_page: origin.origin_page,
    previous_page: origin.previous_page,
    landing_path: origin.landing_path,
    ...readUtms(),
    ...readTrackingSnapshot(formId),
    ...readAdIdentitySnapshot(),
  };
  for (const [k, v] of Object.entries(out)) {
    out[k] = typeof v === "string" ? v : v == null ? "" : String(v);
  }
  return out;
}

export async function submitLead(
  input: LeadInput,
  source: LeadSource,
  locale: string = "pt",
  webhookUrl: "main" | "whatsapp" = "main",
  forms: string = "",
) {
  const user = leadSchema.parse(input);

  const utms = readUtms();
  const meta = readCaptureMeta(source);
  const formId = `kentro-${source}`;
  const tracking = readTrackingSnapshot(formId);
  const phone = parsePhoneBR(user.phone);
  // Origem real do visitante (campanha/orgânico/IA/página interna), resolvida em
  // cascata no client. `source` do webhook = origem; o formulário vai em form_source.
  const origin = resolveOrigin();
  const adIdentity = readAdIdentitySnapshot();

  // fields.phone deve conter apenas DDD + número do cliente (sem DDI 55).
  // Ex.: "11912345678" ou "38332208706". Fallback para os dígitos brutos quando
  // a normalização não conseguir extrair o número nacional.
  const phoneNational = phone.national || phone.digits;

  const fields = {
    name: user.name,
    company: user.company ?? "",
    phone: phoneNational,
    phone_e164: phone.e164,
    phone_ddd: phone.ddd,
    email: user.email,
    message: user.message ?? "",
    forms,
    source: origin.source,
    form_source: source,
    attribution_method: origin.attribution_method,
    is_campaign: String(origin.isCampaign),
    origin_page: origin.origin_page,
    previous_page: origin.previous_page,
    landing_path: origin.landing_path,
    locale,
    ...utms,
    ...tracking,
    ...adIdentity,
  };


  const rawPayload = {
    fields,
    ...meta,
  };

  // Garante contrato estável: chaves exatas, todos os automáticos como string.
  const payload = webhookPayloadSchema.parse(rawPayload);

  // Envio passa pelo servidor (ficha assinada + prova de trabalho + limite por IP).
  const { takeFormGuard } = await import("@/lib/form-guard-client");
  const guard = await takeFormGuard();
  const hp = Array.from(document.querySelectorAll<HTMLInputElement>('input[name="hp_url"]'))
    .map((i) => i.value)
    .join("");
  const res = await fetch("/api/public/lead", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      webhook: webhookUrl === "whatsapp" ? "whatsapp" : "main",
      token: guard.token,
      nonce: guard.nonce,
      signals: guard.signals,
      hp,
      payload,
    }),
  });

  if (!res.ok) {
    throw new Error(`Envio recusado (${res.status})`);
  }

  // Conversão de lead no pixel do ChatGPT Ads (+ Conversions API).
  try {
    const { trackLeadCreated } = await import("@/lib/analytics");
    trackLeadCreated(source);
  } catch {
    /* noop */
  }
  return true;
}


/**
 * Grava o lead (com atribuição de campanha) no banco da Kentro Cloud.
 * Fire-and-forget: qualquer falha é silenciosa e nunca bloqueia o envio ao webhook.
 */
export function recordLeadInDb(fields: Record<string, string>): void {
  try {
    void import("@/lib/lead-conversions.functions")
      .then(({ recordLeadConversion }) => recordLeadConversion({ data: fields }))
      .catch(() => {
        /* noop */
      });
  } catch {
    /* noop */
  }
}

if (typeof window !== "undefined") {
  void import("@/lib/form-guard-client").then((m) => m.prepareFormGuard());
}
